import type { Coding, Identifier, Observation } from "@medplum/fhirtypes";
import { asMemberId, type MemberId } from "@repo/domain";
import { codeSystemUrl, extensionUrl } from "@repo/fhir-systems";
import { TREATMENT_PROCEDURE_KIND_SYSTEM } from "@repo/fhir-systems/code-systems";
import { OBSERVATION_LEVEL_URL } from "@repo/fhir-systems/extensions";
import { fp } from "./codec";
import { required } from "./read";

/**
 * The storage vocabulary the treatment procedure's three codecs share.
 *
 * THE ORDER LINK KEEPS THE EXISTING CONVENTION UNCHANGED: `basedOn` carries the
 * order-set member reference plus the `ORDER_ID_URL` identifier, and the two
 * extensions restate the pair as plain strings so a reader that never resolves
 * the reference can still say which order this fulfils.
 */

/**
 * The SAME tag and identity system the entity-mapper uses. Deliberate: the
 * stored row does not change shape identity when the model behind it does, so
 * the cutover is a code swap rather than a data migration.
 */
export const TREATMENT_PROCEDURE_KIND = "treatment-procedure";

/** Child rows carry their own kind, so a `part-of` revinclude can be demultiplexed positively. */
export const TREATMENT_OBSERVATION_KIND = "treatment-observation";
export const TREATMENT_UNCAPTURED_KIND = "treatment-observation-uncaptured";

export const TREATMENT_ORDER_SET_ID_URL = extensionUrl("treatment-procedure-order-set-identity");
export const TREATMENT_ORDER_ID_URL = extensionUrl("treatment-procedure-order-identity");
export const TREATMENT_TRIAL_KIND = "treatment-trial";
export const TREATMENT_TRIAL_SOURCE_URL = extensionUrl("treatment-trial-source");
export const TREATMENT_TRIAL_PARAMETER_SYSTEM = codeSystemUrl("treatment-trial-parameter");

export const TREATMENT_OBSERVATION_DEFINITION_CODE_SYSTEM = codeSystemUrl("treatment-observation-definition");
export const TREATMENT_OBSERVATION_COMPONENT_CODE_SYSTEM = codeSystemUrl("treatment-observation-component");

export const OWNED_PROCEDURE_EXTENSION_URLS = new Set<string>([
  OBSERVATION_LEVEL_URL,
  TREATMENT_ORDER_SET_ID_URL,
  TREATMENT_ORDER_ID_URL,
]);

/**
 * Every field the model can EMPTY. `statusReason` is never written any more
 * and stays listed so a reason stored before it was dropped is cleared on the
 * next write; `performer` and `performedPeriod` are absent on
 * `didNotPerform`, and an observation's `component` list is rewritten
 * wholesale by a correction. Trial values live on child Observations.
 *
 * Both Observation kinds declare the same list, and the registry unions them
 * per resource type: a row rewritten from one kind into the other has to clear
 * the marker the other kind left, and an owned field named by only one of them
 * would survive that rewrite.
 */
export const PROCEDURE_OWNS = ["basedOn", "statusReason", "performedPeriod", "performer", "extension", "note"].map(fp);
export const TREATMENT_OBSERVATION_OWNS = ["component", "dataAbsentReason", "extension", "note"].map(fp);

export function kindTag(kind: string): Coding {
  return { system: TREATMENT_PROCEDURE_KIND_SYSTEM, code: kind };
}

export function kindOf(resource: { meta?: { tag?: Coding[] } }): string | undefined {
  return resource.meta?.tag?.find((tag) => tag.system === TREATMENT_PROCEDURE_KIND_SYSTEM)?.code;
}

/** This aggregate's kind marker, replacing whatever kind the stored row claimed. */
export function kindTags(prior: { meta?: { tag?: Coding[] } } | null, kind: string): Coding[] {
  return [...(prior?.meta?.tag ?? []).filter((tag) => tag.system !== TREATMENT_PROCEDURE_KIND_SYSTEM), kindTag(kind)];
}

/** Stable member identity for a child row, under the identity system of its kind. */
export function memberIdentity(row: Observation, system: string): string | undefined {
  return row.identifier?.find((identifier) => identifier.system === system)?.value;
}

export function memberIdentifiersToFhir(value: string, system: string, prior: Observation | null): Identifier[] {
  const existing = prior?.identifier?.find((identifier) => identifier.system === system);
  if (existing?.value === value && prior?.identifier) return prior.identifier;
  return [{ system, value }, ...(prior?.identifier ?? []).filter((identifier) => identifier.system !== system)];
}

/** Child rows cannot infer their patient, visit or owner; the mapper supplies these resolved graph links. */
export interface TreatmentObservationRowContext {
  subject: Observation["subject"];
  encounter: Observation["encounter"];
  /** The owning procedure, by locator and identity. */
  procedure: NonNullable<Observation["partOf"]>[number];
}

/** Which declared observation a row realises. Both kinds name it the same way. */
export function definitionIdOf(row: Observation): MemberId {
  return asMemberId(
    required(
      row,
      "definitionId",
      row.code.coding?.find((coding) => coding.system === TREATMENT_OBSERVATION_DEFINITION_CODE_SYSTEM)?.code,
    ),
  );
}
